export type MockChunkType =
    | 'body'
    | 'abstract'
    | 'table_caption'
    | 'figure_caption'
    | 'supplemental_caption';

export interface MockSearchItem {
    id: string;
    title: string;
    document_id: string;
    doi?: string;
    issn?: string;
    document_scope: 'body' | 'caption_only';
    formula_references?: { label: string; image_path: string }[];
    authors: string[];
    journal: string;
    year: number;
    language: string;
    field: string;
    section: string;
    page: number;
    chunk_type: MockChunkType;
    similarity_score: number;
    cross_score: number;
    keyword_score: number;
    search_score: number;
    ocr_confidence: number;
    keywords: string[];
    content: {
        previous: string;
        current: string;
        next: string;
    };
}

export const MOCK_SEARCH_RESPONSE = {
    request_id: 'mock-search-20260727-001',
    generated_at: '2026-07-27T04:00:00Z',
    timings: {
        keyword: {
            keyword_ms: 18.6,
            bi_encoder_ms: 0,
            cross_encoder_ms: 0,
            total_ms: 24.9
        },
        dense: {
            keyword_ms: 0,
            bi_encoder_ms: 43.2,
            cross_encoder_ms: 0,
            total_ms: 50.7
        },
        hybrid: {
            keyword_ms: 17.8,
            bi_encoder_ms: 42.6,
            cross_encoder_ms: 13.9,
            total_ms: 81.4
        }
    },
    results: [
        {
            id: 'chunk-KISTI-2024-001-0142',
            title: '의료 영상 진단에서 설명 가능한 인공지능 모델의 임상 적용 가능성',
            document_id: 'KISTI-ART-2024-001874',
            doi: '10.23001/kjmi.2024.18.2.142',
            issn: '2093-3681',
            document_scope: 'body',
            formula_references: [
                {
                    label: '식 (12)',
                    image_path: '/scienceon_custom_ocr/KISTI-ART-2024-001874/formula/eq-12.png'
                }
            ],
            authors: ['김서현', '박민준', '이정우'],
            journal: '한국의료정보학회지',
            year: 2024,
            language: '한국어',
            field: '의생명',
            section: '결과',
            page: 142,
            chunk_type: 'body' as MockChunkType,
            similarity_score: 0.9472,
            cross_score: 6.8473,
            keyword_score: 0.8864,
            search_score: 0.9328,
            ocr_confidence: 0.987,
            keywords: ['의료 영상', '설명 가능한 인공지능', '임상 적용', 'XAI'],
            content: {
                previous: '본 연구는 흉부 X-ray와 뇌 MRI 데이터셋을 대상으로 설명 가능한 인공지능 기법의 성능을 비교하였다. 모델별 예측 정확도와 시각적 설명의 일관성을 함께 평가하였다.',
                current: 'Grad-CAM 기반 시각화와 임상 지식 그래프를 결합한 모델은 진단 정확도 94.8%를 유지하면서도 의료진이 병변 근거를 확인할 수 있는 설명을 제공했다. 특히 오탐 사례 검토 과정에서 설명 지도와 판독 소견의 일치도가 기존 모델보다 유의하게 높았다.',
                next: '의료진 설문에서는 설명의 명확성과 업무 활용성에 대한 평균 만족도가 각각 4.31점과 4.18점으로 나타났다. 다만 다기관 외부 검증과 실제 진료 환경에서의 전향적 평가가 추가로 필요하다.'
            }
        },
        {
            id: 'chunk-KISTI-2023-014-0001',
            title: 'A Review of Explainable Deep Learning for Medical Image Analysis',
            document_id: 'KISTI-ART-2023-006421',
            doi: '10.23002/jber.2023.44.1.1',
            document_scope: 'body',
            authors: ['J. Lee', 'S. Choi', 'M. Han'],
            journal: 'Journal of Biomedical Engineering Research',
            year: 2023,
            language: '영어',
            field: '의생명',
            section: '초록',
            page: 1,
            chunk_type: 'abstract' as MockChunkType,
            similarity_score: 0.9237,
            cross_score: 6.2981,
            keyword_score: 0.7612,
            search_score: 0.8995,
            ocr_confidence: 0.994,
            keywords: ['medical imaging', 'deep learning', 'explainability', 'review'],
            content: {
                previous: 'This is the first indexed chunk of the document.',
                current: 'This review summarizes explainable deep learning methods used in radiology, pathology, and ophthalmology. We compare post-hoc visualization, concept-based explanations, and intrinsically interpretable networks with respect to clinical validity and reproducibility.',
                next: 'The reviewed studies show rapid performance gains, while external validation and human-centered evaluation remain limited. We propose a reporting checklist for clinically actionable explanations.'
            }
        },
        {
            id: 'chunk-KISTI-2024-051-0003',
            title: '리튬이온전지 열폭주 조기 예측을 위한 멀티모달 머신러닝 모델',
            document_id: 'KISTI-ART-2024-009733',
            issn: '1229-1935',
            document_scope: 'body',
            formula_references: [
                {
                    label: '식 (3)',
                    image_path: '/scienceon_custom_ocr/KISTI-ART-2024-009733/formula/eq-03.png'
                }
            ],
            authors: ['정도윤', '한가람', '최유진'],
            journal: '전기화학회지',
            year: 2024,
            language: '한국어',
            field: '에너지',
            section: '초록',
            page: 3,
            chunk_type: 'abstract' as MockChunkType,
            similarity_score: 0.9184,
            cross_score: 5.9518,
            keyword_score: 0.9121,
            search_score: 0.9168,
            ocr_confidence: 0.981,
            keywords: ['리튬이온전지', '열폭주', '머신러닝', '조기 예측'],
            content: {
                previous: '본 문서는 초록 청크부터 색인되어 이전 청크가 없습니다.',
                current: '전압, 표면 온도, 음향 방출 신호를 결합한 멀티모달 학습으로 리튬이온전지 열폭주를 조기 예측하였다. 제안 모델은 단일 센서 모델 대비 경보 시점을 평균 7.4분 앞당겼고 F1-score 0.93을 기록했다.',
                next: '실험은 세 종류의 원통형 셀과 서로 다른 충전 상태에서 수행되었다. 센서 누락 상황에서도 견고한 예측을 제공하도록 모달리티 드롭아웃을 적용하였다.'
            }
        },
        {
            id: 'chunk-KISTI-2022-088-0017',
            title: '기후변화 시나리오에 따른 국내 벼 생산성 변화 분석',
            document_id: 'KISTI-ART-2022-003208',
            issn: '1229-5671',
            document_scope: 'caption_only',
            authors: ['오현아', '서진호'],
            journal: '한국농림기상학회지',
            year: 2022,
            language: '한국어',
            field: '농림수산',
            section: '결과',
            page: 88,
            chunk_type: 'table_caption' as MockChunkType,
            similarity_score: 0.9048,
            cross_score: 5.4826,
            keyword_score: 0.8742,
            search_score: 0.8971,
            ocr_confidence: 0.956,
            keywords: ['기후변화', '벼 생산성', 'RCP 시나리오', '농업'],
            content: {
                previous: '중부·남부·제주 권역별 기후 자료를 작물 생육 모형에 입력하여 2030년대부터 2090년대까지 생산량 변화를 모의하였다.',
                current: '표 4. SSP2-4.5 및 SSP5-8.5 시나리오에서 기준기간 대비 권역별 벼 수량 변화율. 괄호 안의 값은 30회 앙상블 모의의 표준편차이며, 별표는 95% 신뢰수준에서 유의한 차이를 의미한다.',
                next: 'SSP5-8.5 조건에서 2070년대 남부 지역의 평균 수량 감소폭이 가장 크게 나타났으며, 고온 회피형 이앙 조정은 감소폭을 약 6.2%p 완화했다.'
            }
        },
        {
            id: 'chunk-KISTI-2021-219-0008',
            title: '산업 부문별 탄소중립 전환 기술의 감축 잠재량 비교',
            document_id: 'KISTI-ART-2021-011052',
            doi: '10.23003/jct.2021.12.4.219',
            document_scope: 'caption_only',
            formula_references: [
                {
                    label: '식 (7)',
                    image_path: '/scienceon_custom_ocr/KISTI-ART-2021-011052/formula/eq-07.png'
                }
            ],
            authors: ['배정민', '송혜림', 'R. Patel'],
            journal: 'Journal of Climate Technology',
            year: 2021,
            language: '영어',
            field: '환경',
            section: '방법론',
            page: 219,
            chunk_type: 'figure_caption' as MockChunkType,
            similarity_score: 0.8916,
            cross_score: 4.9738,
            keyword_score: 0.8298,
            search_score: 0.8802,
            ocr_confidence: 0.428,
            keywords: ['탄소중립', '산업 전환', '감축 잠재량', 'CCUS'],
            content: {
                previous: '철강, 시멘트, 석유화학, 수송 부문의 기준 배출량과 기술 도입 비용을 공통 가정으로 환산하여 한계감축비용을 산정하였다.',
                current: 'Figure 6. Sectoral emission-reduction potential by technology portfolio in 2030 and 2050. The shaded interval represents uncertainty from energy-price and technology-learning assumptions.',
                next: 'Electrification provides the largest near-term reduction in transport, whereas hydrogen-based processes and carbon capture account for most reductions in heavy industry after 2040.'
            }
        },
        {
            id: 'chunk-KISTI-2020-077-0024',
            title: '해양 미세플라스틱 자동 분류를 위한 분광 영상 데이터셋 구축',
            document_id: 'KISTI-ART-2020-005901',
            document_scope: 'caption_only',
            authors: ['윤지수', '권태형', '노수빈'],
            journal: '한국해양환경·에너지학회지',
            year: 2020,
            language: '한국어',
            field: '환경',
            section: '부록',
            page: 77,
            chunk_type: 'supplemental_caption' as MockChunkType,
            similarity_score: 0.8531,
            cross_score: 3.8842,
            keyword_score: 0.5924,
            search_score: 0.8017,
            ocr_confidence: 0.482,
            keywords: ['미세플라스틱', '분광 영상', '데이터셋', '자동 분류'],
            content: {
                previous: '부록 A는 시료 채취 지점과 전처리 절차, 촬영 장비의 파장 보정 정보를 정리한다.',
                current: 'Supplementary Figure S3. 폴리에틸렌, 폴리프로필렌, 폴리스타이렌 입자의 파장별 정규화 반사 스펙트럼. 실선은 평균, 음영은 시료 간 표준편차를 나타낸다.',
                next: '부록 B에서는 공개 데이터셋의 파일 구조와 클래스 레이블, 학습·검증 분할 기준을 제공한다.'
            }
        },
        {
            id: 'chunk-KISTI-2023-312-0042',
            title: 'Human–Robot Collaboration Safety Assessment Using Digital Twins',
            document_id: 'KISTI-ART-2023-008311',
            issn: '2758-9412',
            document_scope: 'body',
            authors: ['Y. Nakamura', 'H. Kim', 'T. Sato'],
            journal: 'International Journal of Smart Manufacturing',
            year: 2023,
            language: '일본어',
            field: '기계',
            section: '고찰',
            page: 312,
            chunk_type: 'body' as MockChunkType,
            similarity_score: 0.8429,
            cross_score: 3.6427,
            keyword_score: 0.5106,
            search_score: 0.7794,
            ocr_confidence: 0.967,
            keywords: ['digital twin', 'robot safety', 'smart manufacturing'],
            content: {
                previous: '協働ロボットの動作軌跡と作業者の位置情報をデジタルツイン環境へ同期し、接近速度と停止距離を評価した。',
                current: '提案手法は、実機を停止せずに危険シナリオを反復検証できる。安全距離の動的調整により、固定しきい値方式と比較して作業待ち時間を18%削減した。',
                next: '今後はセンサ遅延と遮蔽による位置推定誤差を含む確率的安全モデルへの拡張が必要である。'
            }
        },
        {
            id: 'chunk-KISTI-2025-033-0109',
            title: '고해상도 기후자료를 활용한 한반도 과수 냉해 위험도 전망',
            document_id: 'KISTI-ART-2025-000918',
            doi: '10.23004/climate.2025.20.1.33',
            document_scope: 'body',
            authors: ['문채원', '임성훈', '조은솔'],
            journal: '기후연구',
            year: 2025,
            language: '한국어',
            field: '농림수산',
            section: '서론',
            page: 33,
            chunk_type: 'body' as MockChunkType,
            similarity_score: 0.8793,
            cross_score: 4.3215,
            keyword_score: 0.8128,
            search_score: 0.8642,
            ocr_confidence: 0.996,
            keywords: ['기후변화', '과수', '냉해', '한반도'],
            content: {
                previous: '최근 개화 시기가 앞당겨지면서 봄철 이상저온에 노출되는 과수 면적이 증가하고 있다.',
                current: '본 연구는 1km 해상도의 기후 시나리오와 생물계절 모형을 결합하여 사과와 배 주산지의 개화기 냉해 위험 변화를 전망한다. 기온 상승이 평균적인 서리일수를 줄이더라도 조기 개화로 인한 취약 기간은 일부 내륙 지역에서 확대될 수 있다.',
                next: '과수 품종별 저온 임계값과 재배지 고도 자료를 반영하여 2030년대, 2050년대, 2080년대의 위험도를 산정하였다.'
            }
        }
    ] as MockSearchItem[]
};
